import axiosInstance from "@/lib/axiosInstance";
import type { CustomAxiosRequestConfig } from "@/types/common.types";
import { accessEndpoints } from "./access.endpoints";
import type { ScopeBox } from "./access.types";
import type { UserScopeCompany } from "./userScope.types";

/** A box the walk-through can write. `entities` has no write route and never needs one. */
export type WritableBox = Exclude<ScopeBox, "entities">;

export type ScopeOption = { id: string; label: string; value: string };

export type CategoryBox = Exclude<WritableBox, "clients">;

export const fetchScopeOptions = async (
  params: { tenantId: string; box: WritableBox; search?: string; page?: number; pageSize?: number },
  signal?: AbortSignal,
): Promise<{ options: ScopeOption[]; total: number; page: number; totalPages: number }> => {
  const { data } = await axiosInstance.get(accessEndpoints.scopeOptions, {
    signal,
    suppressErrorAlert: true,
    params: {
      tenant: params.tenantId,
      box: params.box,
      ...(params.search ? { search: params.search } : {}),
      ...(params.page ? { page: params.page } : {}),
      ...(params.pageSize ? { pageSize: params.pageSize } : {}),
    },
  } as CustomAxiosRequestConfig);
  return {
    options: Array.isArray(data?.data) ? data.data : [],
    total: typeof data?.total === "number" ? data.total : 0,
    page: typeof data?.page === "number" ? data.page : 1,
    totalPages: typeof data?.totalPages === "number" ? data.totalPages : 1,
  };
};

export type ScopeWrite =
  | { op: "setClients"; mode: "all" | "chosen"; clientIds: string[] }
  | { op: "clearClients" }
  | { op: "setRule"; box: WritableBox; type: "inclusion" | "exclusion"; ids: string[] }
  | { op: "removeRule"; box: WritableBox };

/**
 * Every PUT answers with the company's recomputed view, so the engine's own `emptyRequired` is
 * available without a second read. A DELETE answers 204 and returns nothing.
 */
const runWrite = async (
  userId: string,
  tenantId: string,
  write: ScopeWrite,
): Promise<UserScopeCompany | null> => {
  const quiet = { suppressErrorAlert: true } as CustomAxiosRequestConfig;
  switch (write.op) {
    case "setClients": {
      const { data } = await axiosInstance.put(
        accessEndpoints.clientAssignment(userId, tenantId),
        { mode: write.mode, clientIds: write.clientIds },
        quiet,
      );
      return data?.data ?? null;
    }
    case "clearClients":
      await axiosInstance.delete(accessEndpoints.clientAssignment(userId, tenantId), quiet);
      return null;
    case "setRule": {
      const { data } = await axiosInstance.put(
        accessEndpoints.scopeRule(userId, tenantId, write.box),
        { type: write.type, ids: write.ids },
        quiet,
      );
      return data?.data ?? null;
    }
    case "removeRule":
      await axiosInstance.delete(accessEndpoints.scopeRule(userId, tenantId, write.box), quiet);
      return null;
  }
};

export type EntitySaveResult = {
  tenantId: string;
  tenantName: string;
  written: number;
  failed: ScopeWrite[];
  /** The engine's own answer after the last PUT, when there was one. */
  view: UserScopeCompany | null;
};

export const stillEmptyRequired = (view: UserScopeCompany | null): boolean =>
  view !== null && view.boxes.some((box) => box.emptyRequired);

/**
 * Applies one entity's changes in order and keeps going when one box fails, so a failure on the
 * third box does not silently abandon the fourth. The caller reports which boxes did not land;
 * a partial save is a real state here, not an error to swallow.
 */
export const saveEntityScope = async (
  userId: string,
  company: UserScopeCompany,
  writes: ScopeWrite[],
): Promise<EntitySaveResult> => {
  const result: EntitySaveResult = {
    tenantId: company.tenantId,
    tenantName: company.tenantName,
    written: 0,
    failed: [],
    view: null,
  };

  for (const write of writes) {
    try {
      const view = await runWrite(userId, company.tenantId, write);
      if (view) result.view = view;
      result.written += 1;
    } catch {
      result.failed.push(write);
    }
  }
  return result;
};
